import { IFileAssociation } from '@raas/shared/src/types/file-association';
import { IDocumentElementDetails, IDocumentElementDelta } from './document-element';
import { ITag } from './tag';

export enum LegalProvisionTypeEnum {
  LAWS_AND_REGULATIONS = 'Laws and Regulations',
  OTHER_LEGAL_DOCUMENTS = 'Other Legal Documents',
  OTHER_DOCUMENTS = 'Other Documents',
  STANDARD = 'Standard',
  GUIDELINE = 'Guideline',
  CODE = 'Code',
  SPECIFICATION = 'Specification',
}

export enum LegalProvisionContentType {
  LIGHT = 'LIGHT',
  FULL = 'FULL',
}

export type LegalProvisionType = `${LegalProvisionTypeEnum}`;

export enum LegalProvisionStatus {
  NONE = '-',
  DRAFT = 'Draft',
  ACTIVE = 'Active',
  WITHDRAWN = 'Withdrawn',
  REPEALED = 'Repealed',
  SUPERSEDED = 'Superseded',
  AMENDED = 'Amended',
}

export type LegalProvisionStatusType = `${LegalProvisionStatus}`;

export type IReadLegalProvision = {
  id: string;
  entityId: string;
  fullName: string;
  abbreviation: string;
  originalVersion: string;
  validFrom?: Date | null;
  validTo?: Date | null;
  dateOfPublication?: Date | null;
  dateOfAdoption?: Date | null;
  publisher?: string | null;
  type: LegalProvisionType;
  sourceName: string;
  sourceLink: string;
  tags?: ITag[];
  versionId: string;
  versionName: string;
  versionTimestamp: Date;
  releaseId: string;
  releaseTimestamp: Date;
  isComplete: boolean;
  countryFlag: string;
  changeLog: string;
  notes: string;
  translations?: ILegalProvisionTranslation[];
  legalProvisionContent: LegalProvisionContentType;
  attachments?: IFileAssociation[];
  countryLabel?: string;
  status?: LegalProvisionStatusType;
  summary?: string;
  validToRaw?: string;
  validFromRaw?: string;
  dateOfPublicationRaw?: string;
  summaryOfChanges?: string;
};

export type IWriteLegalProvision = {
  id: string;
  fullName: string;
  abbreviation: string;
  originalVersion: string;
  validFrom?: Date | null;
  validTo?: Date | null;
  dateOfPublication?: Date | null;
  dateOfAdoption?: Date | null;
  publisher?: string | null;
  type: LegalProvisionType;
  sourceName: string;
  sourceLink: string;
  tagIds?: string[];
  versionName: string;
  releaseId: string;
  isComplete: boolean;
  countryFlag: string;
  notes: string;
  legalProvisionContent: LegalProvisionContentType;
  status?: LegalProvisionStatusType;
  summary?: string;
  validToRaw?: string;
  validFromRaw?: string;
  dateOfPublicationRaw?: string;
  summaryOfChanges?: string;
};

export type ILegalProvisionDetails = IReadLegalProvision & {
  rootElement: IDocumentElementDetails;
};

export type GroupedLegalProvisions = Record<string, Record<string, IReadLegalProvision[]>>;

export enum LegalProvisionTranslationType {
  ORIGINAL = 'ORIGINAL',
  AI = 'AI',
}

export type ILegalProvisionTranslation = {
  translationId: string;
  legalProvisionId: string;
  language: string;
  translationType: LegalProvisionTranslationType;
  publisher: string;
  sourceName: string;
  sourceLink: string;
  notes: string;
  isDefault: boolean;
  isHidden: boolean;
  fieldsTranslated?: string[];
};

export type LegalProvisionGroupedVersionType = {
  versionId: string;
  versionName: string;
  versionTimestamp: Date;
  releases: IReadLegalProvision[];
};

export type RegulationWrite = {
  id: string;
  fullName: string;
  abbreviation: string;
  originalVersion: string;
  validFrom?: Date | null;
  validTo?: Date | null;
  dateOfPublication?: Date | null;
  dateOfAdoption?: Date | null;
  publisher?: string | null;
  type: LegalProvisionType;
  sourceName: string;
  sourceLink: string;
  tagIds?: string[];
  versionName: string;
  releaseId: string;
  isComplete: boolean;
  countryFlag: string;
  notes: string;
  legalProvisionContent: LegalProvisionContentType;
  translationId?: string;
  language: string;
  translationType: LegalProvisionTranslationType;
  status?: LegalProvisionStatusType;
  summary?: string;
  validToRaw?: string;
  validFromRaw?: string;
  dateOfPublicationRaw?: string;
  summaryOfChanges?: string;
};

export type ILegalProvisionDeltaDetails = IReadLegalProvision & {
  rootElement: IDocumentElementDelta;
};
